/**
 * Bots diario — 3A bots dashboard
 *
 * Renders 2 sub-tabs from pre-computed impact_serving.dashboard (kind=bots) data.
 * Front computes NOTHING beyond en-US formatting, axis drawing, and
 * the 7-day trailing mean overlay. All figures are read directly from JSON.
 *
 * Data-science ref: b797bd58780f42fb17808170b8d19c9d65dac954
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Bar,
  BarChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import type { BotsDashboardData } from '@/services/impactDashboardService';

// ── en-US formatting ──────────────────────────────────────────────────────────

const esES = new Intl.NumberFormat('en-US');
const esESCur = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const esESPct = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

function fmtN(n: number | null | undefined): string {
  if (n == null) return '—';
  return esES.format(n);
}
function fmtEur(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${esESCur.format(n)} €`;
}
function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${esESPct.format(n)} %`;
}
function dayLabel(d: string): string {
  const date = new Date(`${d}T12:00:00`);
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}
function periodLabel(p: string): string {
  // Could be a date or period label like 'S01 2026' — return as-is for non-dates
  if (/^\d{4}-\d{2}-\d{2}$/.test(p)) {
    return new Date(`${p}T12:00:00`).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  }
  return p;
}

// ── 7-day trailing mean (only allowed front computation) ──────────────────────

function trailing7dMean(values: number[]): (number | null)[] {
  return values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - 6), i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

// ── Build bot/store filter key ────────────────────────────────────────────────

function botStoreKey(botOpt: string, storeOpt: string): string {
  return `${botOpt}\t${storeOpt}`;
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

// Sentinel used as Select value for "all / no filter" — Radix rejects empty string.
// Shared tooltip props — keeps label text dark on Recharts' white box in class-based dark mode.
export const IMPACT_TOOLTIP_PROPS = {
  contentStyle: { background: '#fff', color: '#111827' },
  labelStyle: { color: '#111827' },
} as const;

const ALL_OPT = '__all__';

// Storefront base for top-product absolute links; paths are store-relative (e.g. /runnerinn/es/-/…/p)
const STOREFRONT_BASE = 'https://www.tradeinn.com';

// Bot family display map — pipeline uses Spanish internal names; unknown values show as-is and log once
const FAM_LABELS: Record<string, string> = {
  Buscador: 'Search engine',
  IA: 'AI',
  Social: 'Social',
  SEO: 'SEO',
};
const _warnedFamilies = new Set<string>();
function famLabel(family: string): string {
  if (!family) return family;
  const mapped = FAM_LABELS[family];
  if (mapped !== undefined) return mapped;
  if (!_warnedFamilies.has(family)) {
    _warnedFamilies.add(family);
    console.warn(`[BotsDiarioTab] unknown bot family: "${family}" — add to FAM_LABELS`);
  }
  return family;
}

// Strip 'fam:' / 'bot:' prefixes for display; translate family names
function optLabel(key: string): string {
  if (!key) return '';
  if (key.startsWith('fam:')) return famLabel(key.slice(4));
  if (key.startsWith('bot:')) return key.slice(4);
  return key;
}

// ── BotFilterSelect ───────────────────────────────────────────────────────────

function BotFilterSelect({
  options,
  value,
  onChange,
  allLabel,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
}) {
  // Skip the '' entry in options — we handle it as the sentinel "all" item
  const filteredOpts = options.filter(o => o !== '');
  return (
    <Select
      value={value === '' ? ALL_OPT : value}
      onValueChange={v => onChange(v === ALL_OPT ? '' : v)}
    >
      <SelectTrigger className="w-[200px] text-xs">
        <SelectValue placeholder={allLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_OPT}>{allLabel}</SelectItem>
        {filteredOpts.map(opt => (
          <SelectItem key={opt} value={opt}>
            {optLabel(opt)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── El rastreo tab (p_datos) ──────────────────────────────────────────────────

export function ElRastreoTab({ data }: { data: BotsDashboardData }) {
  const [botFilter, setBotFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [botFilter2, setBotFilter2] = useState('');
  const [storeFilter2, setStoreFilter2] = useState('');
  const [granularity, setGranularity] = useState<'dia' | 'semana' | 'mes' | 'trimestre'>('dia');
  const [topBotFilter, setTopBotFilter] = useState('');

  const { totals, daily, daily_by, coverage, options, revisit, matrix, top_products, families, stores, bots } = data;

  // Daily chart
  const dailyKey = botStoreKey(botFilter, storeFilter);
  const dailyEntry = daily_by[dailyKey];
  const dailyFilterMissing = dailyEntry == null;
  const dailySeries = dailyFilterMissing ? [] : dailyEntry.requests;
  const dailyMean = trailing7dMean(dailySeries);
  const dailyMediaPerDay = dailyFilterMissing ? undefined : dailyEntry.per_day;
  const dailyTotal = dailyFilterMissing ? undefined : dailyEntry.total;

  const dailyChartData = daily.dates.map((d, i) => ({
    date: d,
    peticiones: dailySeries[i] ?? null,
    media7d: dailyMean[i],
    agents: daily.agents?.[i] ?? null,
  }));

  // Coverage chart
  const covKey = botStoreKey(botFilter2, storeFilter2);
  const covGran = coverage[granularity];
  const covEntry = covGran.by_option[covKey];
  const covFilterMissing = covEntry == null;
  const covSeries = covFilterMissing ? [] : covEntry.products;
  const covPeak = covFilterMissing ? undefined : covEntry.peak;
  const covPeakPct = covFilterMissing ? undefined : covEntry.peak_pct_catalog;

  const covChartData = covGran.periods.map((p, i) => ({
    period: p,
    productos: covSeries[i] ?? null,
  }));

  // Top products: absent key (pipeline gap) → show "sin datos" state, not silent empty list.
  const topKey = topBotFilter;
  const topProducts = topKey in top_products.by_option ? top_products.by_option[topKey] : null;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        <KpiCard label="Total requests" value={fmtN(totals.requests)} />
        <KpiCard label="Crawled products" value={fmtN(totals.products_crawled)} />
        <KpiCard label="Distinct bots" value={fmtN(totals.bots)} />
        <KpiCard label="% Google" value={fmtPct(totals.googlebot_pct)} />
        <KpiCard label="Requests from new system" value={fmtN(totals.requests_after_deploy)} />
        <KpiCard label="% from new system" value={fmtPct(totals.pct_after_deploy)} />
        <KpiCard label="Median requests/day" value={fmtN(totals.median_per_day)} />
        <KpiCard label="Days with data" value={fmtN(totals.days_with_data)} />
        <KpiCard label="AI requests" value={fmtN(totals.ai_requests)} />
        <KpiCard label="% AI" value={fmtPct(totals.ai_pct)} />
        <KpiCard label="AI products" value={fmtN(totals.ai_products)} />
        <KpiCard label="Products from new system" value={fmtN(totals.products_after_deploy)} />
      </div>

      {/* Summary text */}
      <Card>
        <CardContent className="pt-4 text-xs text-muted-foreground space-y-1">
          <p>Data {data.data_from} — {data.data_until} ({fmtN(totals.days_in_period)} days)</p>
          {families.length > 0 && (
            <p>Families: {families.map(f => `${famLabel(f.family)} ${fmtN(f.requests)} requests`).join(' · ')}</p>
          )}
          {stores.length > 0 && (
            <p>Stores: {stores.map(s => `${s.store} ${fmtN(s.requests)}`).join(' · ')}</p>
          )}
          {bots.length > 0 && (
            <p>Bots: {bots.slice(0, 8).map(b => `${b.bot} ${fmtN(b.requests)}`).join(' · ')}{bots.length > 8 ? ' …' : ''}</p>
          )}
        </CardContent>
      </Card>

      {/* Peticiones por día */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Requests per day</CardTitle>
            <div className="flex items-center gap-2">
              <BotFilterSelect
                options={options.bot}
                value={botFilter}
                onChange={setBotFilter}
                allLabel="All bots"
              />
              <BotFilterSelect
                options={options.store}
                value={storeFilter}
                onChange={setStoreFilter}
                allLabel="All stores"
              />
            </div>
          </div>
          {!dailyFilterMissing && (
            <p className="text-xs text-muted-foreground">
              Daily average {fmtN(dailyMediaPerDay)} · Total {fmtN(dailyTotal)} requests
            </p>
          )}
        </CardHeader>
        <CardContent>
          {dailyFilterMissing ? (
            <p className="text-xs text-muted-foreground py-8 text-center">Option not in data source (combination unavailable)</p>
          ) : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={dailyChartData} margin={{ top: 4, right: 40, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={70} tickFormatter={fmtN} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={35} tickFormatter={fmtN} domain={[0, daily.agents_max ?? 'auto']} />
              <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={(v: number, name: string) => [fmtN(v), name === 'media7d' ? '7d avg' : name === 'agents' ? 'active agents' : 'requests']} labelFormatter={dayLabel} />
              {daily.new_system_from && (
                <ReferenceLine yAxisId="left" x={daily.new_system_from} stroke="#f59e0b" strokeDasharray="4 2" label={{ value: 'new system', position: 'top', fontSize: 9 }} />
              )}
              {(daily.marked ?? []).map((m: string) => (
                <ReferenceLine yAxisId="left" key={m} x={m} stroke="#ef444488" strokeDasharray="2 2" />
              ))}
              <Bar yAxisId="left" dataKey="peticiones" name="Requests" fill="#3b82f6" opacity={0.7} radius={[1, 1, 0, 0]} />
              <Line yAxisId="left" dataKey="media7d" stroke="#374151" dot={false} strokeWidth={1.5} connectNulls={false} name="media7d" />
              <Line yAxisId="right" dataKey="agents" stroke="#f59e0b" dot={false} strokeWidth={1.5} connectNulls={false} name="agents" strokeDasharray="4 2" />
            </ComposedChart>
          </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Coverage chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Distinct products with at least one crawl</CardTitle>
            <div className="flex items-center gap-2">
              <BotFilterSelect
                options={options.bot}
                value={botFilter2}
                onChange={setBotFilter2}
                allLabel="All bots"
              />
              <BotFilterSelect
                options={options.store}
                value={storeFilter2}
                onChange={setStoreFilter2}
                allLabel="All stores"
              />
              <Select value={granularity} onValueChange={v => setGranularity(v as typeof granularity)}>
                <SelectTrigger className="w-[130px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">day</SelectItem>
                  <SelectItem value="semana">week</SelectItem>
                  <SelectItem value="mes">month</SelectItem>
                  <SelectItem value="trimestre">quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {covPeak != null && (
            <p className="text-xs text-muted-foreground">
              Peak: {fmtN(covPeak)} products ({fmtPct(covPeakPct)} of catalog)
            </p>
          )}
        </CardHeader>
        <CardContent>
          {covFilterMissing ? (
            <p className="text-xs text-muted-foreground py-8 text-center">Option not in data source (combination unavailable)</p>
          ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={covChartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tickFormatter={periodLabel} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={70} tickFormatter={fmtN} />
              <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={(v: number) => [fmtN(v), 'products']} labelFormatter={periodLabel} />
              {covGran.new_system_from && (
                <ReferenceLine x={covGran.new_system_from} stroke="#f59e0b" strokeDasharray="4 2" />
              )}
              {(covGran.marked ?? []).map((m: string) => (
                <ReferenceLine key={m} x={m} stroke="#ef444488" strokeDasharray="2 2" />
              ))}
              <Bar dataKey="productos" name="Products" fill="#10b981" opacity={0.8} radius={[1, 1, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Revisit frequency table */}
      {revisit.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">How often each bot revisits the same product</CardTitle>
          </CardHeader>
          <CardContent>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Bot</TableHead>
                  <TableHead>Family</TableHead>
                  <TableHead className="text-right">Returns every</TableHead>
                  <TableHead className="text-right">First quarter before</TableHead>
                  <TableHead className="text-right">Third quarter after</TableHead>
                  <TableHead className="text-right">Products</TableHead>
                  <TableHead className="text-right">% catalog</TableHead>
                  <TableHead className="text-right">Views/product/month</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revisit.map(r => (
                  <TableRow key={r.bot}>
                    <TableCell>{r.bot}</TableCell>
                    <TableCell>{famLabel(r.family)}</TableCell>
                    <TableCell className="text-right">{fmtN(r.median_days)} days</TableCell>
                    <TableCell className="text-right">{fmtN(r.p25_days)} days</TableCell>
                    <TableCell className="text-right">{fmtN(r.p75_days)} days</TableCell>
                    <TableCell className="text-right">{fmtN(r.products)}</TableCell>
                    <TableCell className="text-right">{fmtPct(r.pct_catalog)}</TableCell>
                    <TableCell className="text-right">{fmtN(r.visits_per_product_month)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Bot × store matrix */}
      {matrix.rows.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Bot by store</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Bot</TableHead>
                  <TableHead>Family</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  {matrix.stores.map(s => (
                    <TableHead key={s} className="text-right">{s}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {matrix.rows.map(r => (
                  <TableRow key={r.bot}>
                    <TableCell>{r.bot}</TableCell>
                    <TableCell>{famLabel(r.family)}</TableCell>
                    <TableCell className="text-right">{fmtN(r.total)}</TableCell>
                    {matrix.stores.map(s => (
                      <TableCell key={s} className="text-right">{fmtN(r.by_store[s])}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow className="font-semibold border-t-2">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right">{fmtN(matrix.total.total)}</TableCell>
                  {matrix.stores.map(s => (
                    <TableCell key={s} className="text-right">{fmtN(matrix.total.by_store[s])}</TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Top 40 products */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">Top 40 most crawled products</CardTitle>
            <Select
              value={topBotFilter === '' ? ALL_OPT : topBotFilter}
              onValueChange={v => setTopBotFilter(v === ALL_OPT ? '' : v)}
            >
              <SelectTrigger className="w-[200px] text-xs">
                <SelectValue placeholder="All bots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_OPT}>All bots</SelectItem>
                {top_products.options.filter(o => o !== '').map(opt => (
                  <SelectItem key={opt} value={opt}>
                    {optLabel(opt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {topProducts === null ? (
            <p className="text-xs text-muted-foreground py-8 text-center">Option not in data source (combination unavailable)</p>
          ) : (
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Store</TableHead>
                <TableHead className="text-right">Requests</TableHead>
                <TableHead className="text-right">Days</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topProducts.slice(0, 40).map((p, i) => (
                <TableRow key={`${p.item_id}-${i}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="max-w-[300px] truncate">
                    <a
                      href={`${STOREFRONT_BASE}${p.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs underline text-blue-600 hover:text-blue-800"
                    >
                      {p.path}
                    </a>
                  </TableCell>
                  <TableCell>{p.store}</TableCell>
                  <TableCell className="text-right">{fmtN(p.requests)}</TableCell>
                  <TableCell className="text-right">{fmtN(p.days)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Cómo está hecho tab (p_tec) ───────────────────────────────────────────────

export function BotsComoEstaHechoTab({ data }: { data: BotsDashboardData }) {
  const tech = data.technical;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Data pipeline, from database to this page</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-1">
          <p>bot_product_daily.py — fetches data ({fmtN(tech.rows.bot_product_daily)} rows)</p>
          <p>build_dashboard_bots.py — generates this page</p>
          <p>export_bots_table.py — generates the CSV and Excel</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Second query: how many agents reported</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          <p>{fmtN(tech.rows.agents_daily)} rows in agents_daily</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Where each figure comes from</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          <p>Cohort: {fmtN(tech.rows.cohort)} products</p>
        </CardContent>
      </Card>

      {/* SQL queries */}
      {tech.queries.map(q => (
        <Card key={q.name}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{q.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap">
              <code>{q.sql}</code>
            </pre>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── BotsDiarioTab (main export) ───────────────────────────────────────────────

export function BotsDiarioTab({ data }: { data: BotsDashboardData }) {
  return (
    <div className="space-y-2">
      {/* Header badge */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground px-1">
        <span>Data through <strong>{data.data_until}</strong></span>
        <span>·</span>
        <span>Cohort {fmtN(data.cohort_products)} products</span>
      </div>

      <Tabs defaultValue="p_datos">
        <TabsList>
          <TabsTrigger value="p_datos">Crawl data</TabsTrigger>
          <TabsTrigger value="p_tec">How it's built</TabsTrigger>
        </TabsList>

        <TabsContent value="p_datos" className="mt-4">
          <ElRastreoTab data={data} />
        </TabsContent>

        <TabsContent value="p_tec" className="mt-4">
          <BotsComoEstaHechoTab data={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
